import { AppShell, Burger, Group, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";

import { assistantApi } from "#/api/assistantApi";
import { TrexLogo } from "#/components/assistant/TrexLogo";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { useDataHealth } from "#/hook/useDataHealth";
import { adminRoutes, dashboardRoutes } from "#/routes/dashboardRoutes";
import type { DashboardRoute } from "#/types/navigation";
import { UserRole } from "#/types/user";
import { hexToRgba } from "#/utils/ColorUtils";
import { useState } from "react";
import { Outlet } from "react-router-dom";
import LanguageSelector from "../LanguageSelector";
import SideNavButton from "./components/SideNavButton";
import UserMenu from "./components/UserMenu";
import NotificationBell from "./components/NotificationBell";
import { AssistantChatProvider } from "#/providers/AssistantChatProvider";

function DashboardLayout() {
  const { user } = useAuth();
  const { client } = useClient();

  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const [imgError, setImgError] = useState(false);

  const filterRoutesByAllowedRoutes = (routes: typeof dashboardRoutes) => {
    // If user is admin, return all admin routes without filtering
    if (user?.role === UserRole.ADMIN) {
      return routes;
    }

    // If client has no allowedRoutes or it's empty, show all routes (backward compatibility)
    if (!client?.allowedRoutes || client.allowedRoutes.length === 0) {
      return routes;
    }

    // Filter routes based on allowedRoutes
    return routes
      .map((route) => {
        const isParentAllowed = client.allowedRoutes?.includes(route.href);

        // If parent is not allowed, don't show it or its children
        if (!isParentAllowed) {
          return null;
        }

        // If parent is allowed, filter its children
        if (route.children) {
          const filteredChildren = route.children.filter((child) => {
            const childFullPath = route.href + child.href;
            return client.allowedRoutes?.includes(childFullPath);
          });

          // If parent has no allowed children, hide the parent too
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...route,
            children: filteredChildren,
          };
        }

        return route;
      })
      .filter((route) => route !== null);
  };

  const routes: DashboardRoute[] =
    user?.role === UserRole.ADMIN ? adminRoutes : filterRoutesByAllowedRoutes(dashboardRoutes);

  // T-Rex AI is gated by its own per-user config (not allowedRoutes). Show the
  // nav entry only when the assistant is enabled for the current user.
  const { data: assistantConfig } = useQuery({
    queryKey: ["assistant-config"],
    queryFn: async () => (await assistantApi.getConfig()).data,
    staleTime: 5 * 60 * 1000,
  });

  const navRoutes = assistantConfig?.enabled
    ? [...routes, { label: "nav.trexAi", icon: TrexLogo, href: "/t-rex" }]
    : routes;

  // Data Health badge: only for client users who can see the page.
  const DATA_HEALTH_PATH = "/data-health";
  const dataHealthVisible =
    user?.role !== UserRole.ADMIN && routes.some((route) => route.href === DATA_HEALTH_PATH);
  const { data: dataHealth } = useDataHealth(undefined, { enabled: dataHealthVisible });
  const navBadges =
    dataHealthVisible && dataHealth ? { [DATA_HEALTH_PATH]: dataHealth.openCount } : undefined;

  return (
    <AssistantChatProvider>
      <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 230,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group
          h="100%"
          px="md"
          style={{
            background: client?.color
              ? hexToRgba(client?.color, 0.1)
              : "#e3edff",
          }}
        >
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
            color={client?.color ? client?.color : "black"}
          />

          <Burger
            opened={desktopOpened}
            onClick={toggleDesktop}
            visibleFrom="sm"
            size="sm"
            color={client?.color ? client?.color : "black"}
          />
          <div className="w-fit h-full flex items-center">
            {imgError ? (
              <div className="w-fit">
                <Title c={client?.color ?? "black"} order={3}>
                  {client?.name ?? "--"}
                </Title>
              </div>
            ) : (
              <img
                src={client?.logo ?? "/tma-logo-sm.svg"}
                alt="logo"
                onError={() => setImgError(true)}
                className="h-10 object-contain"
              />
            )}
          </div>

          <div className="w-fit h-full py-2  flex items-center ml-auto gap-2">
            <LanguageSelector />
            <NotificationBell />
            <UserMenu />
          </div>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar>
        <div
          className={`w-full h-full p-4 flex flex-col gap-4 overflow-y-auto overflow-x-hidden`}
          style={{
            backgroundColor: client?.color ?? "#3f81ef",
          }}
        >
          {navRoutes.map((item) => (
            <SideNavButton
              key={item.href}
              route={item}
              badges={navBadges}
              onClick={() => {
                toggleMobile();
              }}
            />
          ))}
        </div>
      </AppShell.Navbar>
      <AppShell.Main className="flex flex-col h-[calc(100vh-60px)] overflow-auto w-full">
        <Outlet />
      </AppShell.Main>
      </AppShell>
    </AssistantChatProvider>
  );
}

export default DashboardLayout;
